import React, { useState } from 'react';
import TrendPair from './TrendPair';
import { submitGuess, getPairStats } from '../services/apiService';
import { generatePairHash } from '../utils/hash';
import { TrendPairData, PairStats, Selection } from '../types';

/**
 * Props for the GameBoard component
 */
interface GameBoardProps {
  /**
   * Array of trending search term pairs to display in the game
   * Each pair contains the terms to compare and their popularity data
   */
  trendPairs: TrendPairData[];
}

/**
 * GameBoard: Main game component that orchestrates the game flow
 * 
 * This component is responsible for:
 * 1. Displaying all trend pairs to the user
 * 2. Tracking which pairs the user has selected
 * 3. Sending user selections to the backend API
 * 4. Fetching statistics about other users' guesses
 * 5. Showing feedback and results when the game is complete
 * 
 * The component demonstrates several key React patterns:
 * - State management with useState
 * - Handling asynchronous operations with async/await
 * - Conditional rendering based on game state
 * - Passing callbacks to child components
 * - Error handling for API calls
 */
const GameBoard: React.FC<GameBoardProps> = ({ trendPairs }) => {
  /**
   * State Management
   * ---
   * React's useState hook creates reactive state variables that trigger
   * re-renders when they change. Notice how each piece of state has a clear purpose.
   */
  
  // Record of user's selections for each pair (indexed by pair position)
  const [selectedPairs, setSelectedPairs] = useState<Record<number, Selection>>({});
  
  // Statistics about how other users have guessed for these pairs
  const [pairStats, setPairStats] = useState<Record<string, PairStats>>({});
  
  // Whether all pairs have been answered (game is complete)
  const [gameComplete, setGameComplete] = useState<boolean>(false);
  
  // Today's date for API calls (YYYY-MM-DD format)
  const today = new Date().toISOString().split('T')[0];

  /**
   * Handle user selection of a trend item
   * 
   * This function is passed to child components and called when a user
   * makes a selection. It updates the UI immediately, then sends the
   * selection to the backend API.
   * 
   * @param pairIndex - Index of the pair in the trendPairs array
   * @param isLeft - Whether the user selected the left item (true) or right item (false)
   */
  const handleSelection = async (pairIndex: number, isLeft: boolean) => {
    try {
      // Get the pair data and determine which term was selected
      const pair = trendPairs[pairIndex];
      const selectedTerm = isLeft ? pair.left : pair.right;
      
      // Create new selections object with this selection added
      const updatedSelections: Record<number, Selection> = {
        ...selectedPairs,
        [pairIndex]: isLeft ? 'left' : 'right'
      };
      
      // Update state for immediate UI feedback (optimistic update)
      setSelectedPairs(updatedSelections);
      
      // Send selection to the backend API
      await submitGuess(pair, selectedTerm, today);
      
      // Check if game is complete (all pairs have been answered)
      const allPairsAnswered = Object.keys(updatedSelections).length === trendPairs.length;
      
      if (allPairsAnswered) {
        // If all pairs are answered, fetch statistics about other users' guesses
        try {
          const stats = await getPairStats(trendPairs, today);
          setPairStats(stats);
          setGameComplete(true);
        } catch (statsError) {
          console.error('Error fetching statistics:', statsError);
          // Even on error, still mark game as complete with default stats
          setGameComplete(true);
        }
      }
    } catch (error) {
      console.error('Error handling selection:', error);
    }
  };

  /**
   * Get user selection percentages for a specific pair
   * 
   * This helper function retrieves the statistics for a specific pair
   * using its hash as the lookup key. If no statistics are found, it
   * returns default values.
   * 
   * @param pair - The trend pair to get percentages for
   * @returns Object containing percentages for left and right
   */
  const getPairPercentages = (pair: TrendPairData) => {
    // Generate a unique hash for this pair to look up statistics
    const pairHash = generatePairHash(pair.left, pair.right);
    const stats = pairStats[pairHash];
    
    // If no stats found, return default values
    if (!stats) {
      return { leftPercentage: 0, rightPercentage: 0 };
    }
    
    // Return the actual percentages from stats
    return {
      leftPercentage: stats.leftPercentage,
      rightPercentage: stats.rightPercentage
    };
  };

  /**
   * Component Rendering
   * ---
   * The JSX returned below demonstrates:
   * 1. Conditional rendering based on state (gameComplete)
   * 2. Dynamic styling (progress bar width)
   * 3. List rendering with map()
   * 4. Component composition with props
   * 5. Progress indicators and feedback
   */
  return (
    <div className="game-board" style={{ 
      padding: '0 15px', // Only add horizontal padding
      maxWidth: '100%',
      boxSizing: 'border-box' 
    }}>
      {/* Game progress indicator shows how many pairs have been answered */}
      <div className="game-progress" style={{ marginBottom: '20px', textAlign: 'center' }}>
        <p>
          {Object.keys(selectedPairs).length} of {trendPairs.length} pairs answered
        </p>
        {/* Progress bar visualization */}
        <div style={{ 
          width: '100%', 
          height: '8px', 
          backgroundColor: '#eee', 
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          {/* Filled portion of progress bar - width changes dynamically */}
          <div style={{ 
            width: `${(Object.keys(selectedPairs).length / trendPairs.length) * 100}%`, 
            height: '100%', 
            backgroundColor: '#4A90E2', 
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
      
      {/* Render all trend pairs as a list of TrendPair components */}
      {trendPairs.map((pair, index) => {
        // Get statistics for this pair to display
        const { leftPercentage, rightPercentage } = getPairPercentages(pair);
        
        return (
          <div key={index} className="trend-pair-wrapper">
            <TrendPair
              leftContent={pair.left}
              rightContent={pair.right}
              pairIndex={index}
              selectedSide={selectedPairs[index]}
              correctIsLeft={pair.correctIsLeft}
              onSelection={handleSelection}
              showScores={selectedPairs[index] !== undefined}
              leftScore={pair.averageInterestLeft}
              rightScore={pair.averageInterestRight}
              showPercentages={gameComplete}
              leftPercentage={leftPercentage}
              rightPercentage={rightPercentage}
            />
          </div>
        );
      })}
      
      {/* Game completion message - only shown when all pairs are answered */}
      {gameComplete && (
        <div style={{ 
          marginTop: '20px', 
          textAlign: 'center',
          padding: '15px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          <h2>Game Complete!</h2>
          <p>Thank you for playing. Come back tomorrow for new trends!</p>
        </div>
      )}
    </div>
  );
};

export default GameBoard;